import registryData from './offlineRegistry.json';

export type OfflinePackPhase = 'planned' | 'catalog-ready' | 'map-ready';
export type OfflineLocationCategory = 'station' | 'lodging' | 'visit' | 'airport';
export type OfflineRouteMode = 'walking' | 'transit';

export interface OfflineCityPack {
  id: string;
  city: string;
  country: string;
  phase: OfflinePackPhase;
  version: string | null;
  bounds: readonly [number, number, number, number] | null;
  catalogSha256: string | null;
  basemapUrl?: string | null;
  basemapSha256?: string | null;
  basemapBytes?: number | null;
  basemapSourceBuild?: string | null;
  note: string;
}

export interface OfflineLocation {
  id: string;
  packId: string;
  name: string;
  category: OfflineLocationCategory;
  coordinates: readonly [number, number];
  address: string;
}

export interface OfflineRoute {
  id: string;
  packId: string;
  name: string;
  mode: OfflineRouteMode;
  fromLocationId: string;
  toLocationId: string;
  distanceMeters: number;
  durationMinutes: number;
  geometry: ReadonlyArray<readonly [number, number]>;
}

interface OfflineRegistry {
  schemaVersion: number;
  release: string;
  packs: OfflineCityPack[];
  locations: OfflineLocation[];
  routes: OfflineRoute[];
}

const registry = registryData as unknown as OfflineRegistry;

function validateRegistry(value: OfflineRegistry) {
  if (value.schemaVersion !== 1) throw new Error('Unsupported offline registry schema.');

  const packIds = new Set<string>();
  for (const pack of value.packs) {
    if (packIds.has(pack.id)) throw new Error(`Duplicate offline pack: ${pack.id}`);
    packIds.add(pack.id);
    if (pack.basemapSha256 && !/^[0-9a-f]{64}$/.test(pack.basemapSha256)) throw new Error(`Invalid basemap SHA-256: ${pack.id}`);
    if (pack.basemapBytes != null && (!Number.isInteger(pack.basemapBytes) || pack.basemapBytes <= 0)) throw new Error(`Invalid basemap byte count: ${pack.id}`);
  }

  const locationIds = new Set<string>();
  for (const location of value.locations) {
    if (locationIds.has(location.id)) throw new Error(`Duplicate offline location: ${location.id}`);
    if (!packIds.has(location.packId)) throw new Error(`Unknown pack for location: ${location.id}`);
    const [longitude, latitude] = location.coordinates;
    if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) {
      throw new Error(`Invalid coordinates for location: ${location.id}`);
    }
    locationIds.add(location.id);
  }

  const routeIds = new Set<string>();
  for (const route of value.routes) {
    if (routeIds.has(route.id)) throw new Error(`Duplicate offline route: ${route.id}`);
    if (!packIds.has(route.packId) || !locationIds.has(route.fromLocationId) || !locationIds.has(route.toLocationId)) {
      throw new Error(`Invalid offline route references: ${route.id}`);
    }
    if (route.geometry.length < 2) throw new Error(`Offline route has no geometry: ${route.id}`);
    routeIds.add(route.id);
  }
}

validateRegistry(registry);

export const offlineRegistryVersion = registry.release;
export const offlineCityPacks = registry.packs;
export const offlineLocations = registry.locations;
export const offlineRoutes = registry.routes;
export const offlineLocationById = new Map(offlineLocations.map((location) => [location.id, location]));

const barcelonaPack = offlineCityPacks.find((pack) => pack.id === 'barcelona');
if (!barcelonaPack?.catalogSha256) throw new Error('Barcelona catalog metadata is incomplete.');

const barcelonaLocations = offlineLocations.filter((location) => location.packId === 'barcelona');
const barcelonaRoutes = offlineRoutes.filter((route) => route.packId === 'barcelona');

export const barcelonaOfflinePayload = { locations: barcelonaLocations, routes: barcelonaRoutes };

export const barcelonaRegistrySummary = {
  version: barcelonaPack.version,
  locationCount: barcelonaLocations.length,
  routeCount: barcelonaRoutes.length,
  catalogSha256: barcelonaPack.catalogSha256,
  basemapUrl: barcelonaPack.basemapUrl ?? null,
  basemapSha256: barcelonaPack.basemapSha256 ?? null,
  basemapBytes: barcelonaPack.basemapBytes ?? null,
  basemapSourceBuild: barcelonaPack.basemapSourceBuild ?? null,
};
